/**
 * use-briefing — read + persist hook for operator-authored briefings.
 *
 * Phase 032 Plan 02 (D-06 REQ-BASE-04). Rides the SHIPPED Phase 031 substrate:
 *   - WRITE: `useMutationStore.addBlock` (registered at app-init by
 *     `mutation-client-init.ts`; routes through `cogo.ontology.add_block`
 *     against the live gateway).
 *   - READ:  a direct PostgREST query against `application_ontology`
 *     filtered by the `kind:'briefing'` predicate.
 *
 * Why a direct PostgREST query: the shipped `JsonbAboxSource` was hard-wired
 * to `cobo:Archetype`-shaped triples (`buildInstance` returned null when the
 * subject had no `archetype`). Briefings are a different block-kind that does
 * NOT decompose into a cobo: archetype, so the graph view-model never
 * surfaced them.
 *
 * This hook used to ALSO call `useOntologyGraph` with the five jsonb opts and
 * throw the return value away, on the stated grounds that "the graph cache +
 * Realtime invalidation continue to fire". Measured before removing it, that
 * reason did not hold: `createJsonbAboxSource` closes over its own `cache`
 * variable and `useOntologyGraph` kept the only reference to it in a per-hook
 * `useRef`, so the cache being invalidated was the discarded call's own. The
 * Realtime handler invalidated that private cache, re-ran the same PostgREST
 * read one column wider than `refresh` below already runs, recomputed
 * nodes/edges, and set them into state nobody read. Nothing outside the hook
 * instance could observe any of it — and in particular a Realtime event did
 * NOT refresh the briefing list, because `refresh` was never wired to it.
 * Wiring live refresh is available product work; keeping a graph computation
 * as a way of not doing it was not a side effect, it was a second query.
 *
 * Envelope shape (locked by 032-02-PREVIEW-PROBE.md verdict
 * "canonical accepted" 2026-05-18):
 *
 *   {
 *     id: `briefing-${crypto.randomUUID()}`,
 *     kind: 'briefing',
 *     app_id,
 *     properties: { title, body, author, created_at },
 *   }
 *
 * No fallback envelope is wired here — see 032-02-PREVIEW-PROBE.md §4.
 */

import { useCallback, useEffect, useState } from 'react'
import { useMutationStore } from '@/features/design-system/ontology/mutation-store'
import { supabase } from '@/lib/supabase'
import type { BriefingDraft, BriefingItem } from './types'

interface UseBriefingResult {
  items: BriefingItem[]
  /** True while the initial PostgREST read is in flight (after tenant resolves). */
  loading: boolean
  /** Non-null when the read leg threw. */
  error: Error | null
  /** Re-run the read leg (callable after a successful addBriefing). */
  refresh: () => Promise<void>
  /**
   * Persist a new briefing via the canonical kind:'briefing' envelope.
   *
   * Stamps `author` + `created_at` from the supplied `authorId` + current
   * wall clock; operator only edits `title` + `body`.
   */
  addBriefing: (draft: BriefingDraft, authorId: string) => Promise<void>
}

/**
 * Read briefings + expose addBriefing. When tenantId is null, the read leg is
 * skipped and `items` stays empty — callers MUST gate the UI on the no-tenant
 * state explicitly (the panel renders a `briefing-no-tenant` testid in that
 * case; do NOT pass `tenantId=""` per Phase 031 D-02 — the `ds-surface.tsx`
 * this used to cite was deleted in #2619).
 */
export function useBriefings(
  tenantId: string | null,
  appId: string,
): UseBriefingResult {
  const addBlock = useMutationStore((s) => s.addBlock)

  const [items, setItems] = useState<BriefingItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    if (tenantId === null) {
      setItems([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      // Direct PostgREST: pull all triples for this tenant/app and reduce by
      // subject IRI that contains `/briefing-` (the canonical IRI tail
      // pattern from 032-02-PREVIEW-PROBE.md §2 — subjects look like
      // `https://cogo.xyz/ontology/<tenant>/<app>/<branch>/briefing-<uuid>`).
      const { data, error: pgError } = await supabase
        .schema('cogo')
        .from('application_ontology')
        .select('subject, predicate, object_value')
        .eq('tenant_id', tenantId)
        .eq('app_id', appId)
        .eq('branch', 'main')
      if (pgError) {
        throw new Error(`[use-briefing] PostgREST error: ${pgError.message}`)
      }
      const rows = (data ?? []) as Array<{
        subject: string
        predicate: string
        object_value: string | null
      }>
      setItems(reduceRowsToBriefings(rows))
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)))
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [tenantId, appId])

  // Initial read + re-run when tenant changes.
  useEffect(() => {
    void refresh()
  }, [refresh])

  const addBriefing = useCallback(
    async (draft: BriefingDraft, authorId: string): Promise<void> => {
      if (tenantId === null) {
        throw new Error('[use-briefing] cannot add briefing without tenant')
      }
      const id = `briefing-${crypto.randomUUID()}`
      const briefingDef = {
        id,
        kind: 'briefing',
        app_id: appId,
        properties: {
          title: draft.title,
          body: draft.body,
          author: authorId,
          created_at: new Date().toISOString(),
        },
      }
      await addBlock({
        tenant_id: tenantId,
        app_id: appId,
        block_definition: briefingDef,
      })
      // Optimistic refresh — pull the new briefing from PostgREST so the list
      // reflects the persisted state, not a local-only copy.
      await refresh()
    },
    [tenantId, appId, addBlock, refresh],
  )

  return { items, loading, error, refresh, addBriefing }
}

/**
 * Reduce flat application_ontology triples → BriefingItem[] keyed by subject.
 *
 * The canonical write envelope decomposes into triples where:
 *   - subject IRI contains `/briefing-<uuid>`
 *   - predicate `<base>/properties/title` (or `cogo:title`-style) → object_value = title
 *   - predicate `<base>/properties/body` → object_value = body
 *   - predicate `<base>/properties/author` → object_value = author
 *   - predicate `<base>/properties/created_at` → object_value = created_at
 *
 * Reducer matches on `/briefing-` in the subject IRI then maps predicate tails
 * to the 4 property fields. Subjects without all 4 properties are skipped
 * (defensive — a partial decomposition would surface as a malformed item).
 */
function reduceRowsToBriefings(
  rows: Array<{
    subject: string
    predicate: string
    object_value: string | null
  }>,
): BriefingItem[] {
  const bySubject = new Map<
    string,
    { title?: string; body?: string; author?: string; created_at?: string }
  >()
  for (const row of rows) {
    if (!row.subject.includes('/briefing-')) continue
    const tail = lastSegment(row.predicate)
    if (!tail) continue
    const existing = bySubject.get(row.subject) ?? {}
    if (tail === 'title' && typeof row.object_value === 'string') {
      existing.title = row.object_value
    } else if (tail === 'body' && typeof row.object_value === 'string') {
      existing.body = row.object_value
    } else if (tail === 'author' && typeof row.object_value === 'string') {
      existing.author = row.object_value
    } else if (tail === 'created_at' && typeof row.object_value === 'string') {
      existing.created_at = row.object_value
    }
    bySubject.set(row.subject, existing)
  }

  const out: BriefingItem[] = []
  for (const [subject, props] of bySubject) {
    const id = lastSegment(subject)
    if (!id) continue
    if (
      typeof props.title !== 'string' ||
      typeof props.body !== 'string' ||
      typeof props.author !== 'string' ||
      typeof props.created_at !== 'string'
    ) {
      continue
    }
    out.push({
      id,
      title: props.title,
      body: props.body,
      author: props.author,
      created_at: props.created_at,
    })
  }
  // Newest first by created_at (lexicographic on ISO-8601 == chronological).
  out.sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
  return out
}

function lastSegment(iri: string): string | null {
  const slashIdx = iri.lastIndexOf('/')
  const hashIdx = iri.lastIndexOf('#')
  const idx = Math.max(slashIdx, hashIdx)
  if (idx < 0 || idx === iri.length - 1) return null
  return iri.slice(idx + 1)
}
